/* tslint:disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpRequest, HttpResponse, HttpHeaders } from '@angular/common/http';
import { BaseService as __BaseService } from '../base-service';
import { WebConfiguration as __Configuration } from '../web-configuration';
import { StrictHttpResponse as __StrictHttpResponse } from '../strict-http-response';
import { Observable as __Observable } from 'rxjs';
import { map as __map, filter as __filter } from 'rxjs/operators';

import { Ticgn } from '../models/ticgn';
import { SectionList } from '../models/section-list';
import { SectionSearchOptions } from '../models/section-search-options';
import { SectionCreate } from '../models/section-create';
import { Section } from '../models/section';
import { SectionUpdate } from '../models/section-update';

/**
 * All about the settings of the application
 */
@Injectable({
  providedIn: 'root',
})
class SettingsService extends __BaseService {
  static readonly retrieveTICGNPath = '/settings/ticgn';
  static readonly retrieveSectionsPath = '/settings/sections/search';
  static readonly createSectionPath = '/settings/sections';
  static readonly retrieveSectionPath = '/settings/sections/{uid}';
  static readonly updateSectionPath = '/settings/sections/{uid}';
  static readonly deleteSectionPath = '/settings/sections/{uid}';

  constructor(
    config: __Configuration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Retrieve TICGN
   *
   * Retrieve TICGN
   * @return Success
   */
  retrieveTICGNResponse(): __Observable<__StrictHttpResponse<Array<Ticgn>>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/settings/ticgn`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Array<Ticgn>>;
      })
    );
  }
  /**
   * Retrieve TICGN
   *
   * Retrieve TICGN
   * @return Success
   */
  retrieveTICGN(): __Observable<Array<Ticgn>> {
    return this.retrieveTICGNResponse().pipe(
      __map(_r => _r.body as Array<Ticgn>)
    );
  }

  /**
   * Retrieve delivery sections
   *
   * Retrieve delivery sections
   * @param params The `SettingsService.RetrieveSectionsParams` containing the following parameters:
   *
   * - `search`: The section options object
   *
   * - `offset`: The list items offset
   *
   * - `limit`: The list items limit
   *
   * @return Success
   */
  retrieveSectionsResponse(params: SettingsService.RetrieveSectionsParams): __Observable<__StrictHttpResponse<SectionList>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = params.search;
    if (params.offset != null) __params = __params.set('offset', params.offset.toString());
    if (params.limit != null) __params = __params.set('limit', params.limit.toString());
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/settings/sections/search`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<SectionList>;
      })
    );
  }
  /**
   * Retrieve delivery sections
   *
   * Retrieve delivery sections
   * @param params The `SettingsService.RetrieveSectionsParams` containing the following parameters:
   *
   * - `search`: The section options object
   *
   * - `offset`: The list items offset
   *
   * - `limit`: The list items limit
   *
   * @return Success
   */
  retrieveSections(params: SettingsService.RetrieveSectionsParams): __Observable<SectionList> {
    return this.retrieveSectionsResponse(params).pipe(
      __map(_r => _r.body as SectionList)
    );
  }

  /**
   * create delivery sections
   *
   * Create delivery sections
   * @param sectioncreate The Section create object
   */
  createSectionResponse(sectioncreate: SectionCreate): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = sectioncreate;
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/settings/sections`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * create delivery sections
   *
   * Create delivery sections
   * @param sectioncreate The Section create object
   */
  createSection(sectioncreate: SectionCreate): __Observable<null> {
    return this.createSectionResponse(sectioncreate).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Retrieve a delivery section
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  retrieveSectionResponse(uid: string): __Observable<__StrictHttpResponse<Section>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/settings/sections/${encodeURIComponent(String(uid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Section>;
      })
    );
  }
  /**
   * Retrieve a delivery section
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  retrieveSection(uid: string): __Observable<Section> {
    return this.retrieveSectionResponse(uid).pipe(
      __map(_r => _r.body as Section)
    );
  }

  /**
   * Update a delivery section
   * @param params The `SettingsService.UpdateSectionParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `sectionupdate`: The section update object
   */
  updateSectionResponse(params: SettingsService.UpdateSectionParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    __body = params.sectionupdate;
    let req = new HttpRequest<any>(
      'PUT',
      this.rootUrl + `/settings/sections/${encodeURIComponent(String(params.uid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Update a delivery section
   * @param params The `SettingsService.UpdateSectionParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `sectionupdate`: The section update object
   */
  updateSection(params: SettingsService.UpdateSectionParams): __Observable<null> {
    return this.updateSectionResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Delete a delivery section
   * @param uid The object ID. Must be a valid ID.
   */
  deleteSectionResponse(uid: string): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'DELETE',
      this.rootUrl + `/settings/sections/${encodeURIComponent(String(uid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Delete a delivery section
   * @param uid The object ID. Must be a valid ID.
   */
  deleteSection(uid: string): __Observable<null> {
    return this.deleteSectionResponse(uid).pipe(
      __map(_r => _r.body as null)
    );
  }
}

namespace SettingsService {

  /**
   * Parameters for retrieveSections
   */
  export interface RetrieveSectionsParams {

    /**
     * The section options object
     */
    search?: SectionSearchOptions;

    /**
     * The list items offset
     */
    offset?: number;

    /**
     * The list items limit
     */
    limit?: number;
  }

  /**
   * Parameters for updateSection
   */
  export interface UpdateSectionParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The section update object
     */
    sectionupdate: SectionUpdate;
  }
}

export { SettingsService }
